import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Loader2 } from 'lucide-react';
import { topicsApi, Theme } from '../api/topics';

interface TrainingTheme extends Theme {
  image: string;
  color: string;
  status: 'available' | 'coming-soon';
  path?: string;
  shortDescription: string;
}

// Image mapping based on theme slug
const getImageForTheme = (slug: string): string => {
  switch (slug) {
    case 'ai-geletterdheid':
      return 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&crop=center';
    case 'ai-basics':
    case 'ai-literacy':
      return 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop&crop=center';
    case 'digital-literacy':
      return 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop&crop=center';
    case 'media-literacy':
    case 'mediawijsheid':
      return 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=250&fit=crop&crop=center';
    default:
      return 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop&crop=center';
  }
};

// Short description mapping based on theme slug
const getShortDescriptionForTheme = (slug: string): string => {
  switch (slug) {
    case 'ai-geletterdheid':
      return 'Leer de fundamenten van kunstmatige intelligentie en hoe je het praktisch kunt toepassen in je werk.';
    case 'ai-basics':
    case 'ai-literacy':
      return 'Leer de fundamenten van kunstmatige intelligentie en hoe je het praktisch kunt toepassen.';
    case 'digital-literacy':
      return 'Ontwikkel essentiële digitale vaardigheden voor de moderne werkplek.';
    case 'media-literacy':
    case 'mediawijsheid':
      return 'Leer kritisch omgaan met media, nepnieuws herkennen en verantwoordelijk mediagebruik.';
    default:
      return 'Ontdek nieuwe vaardigheden en verbeter je professionele ontwikkeling.';
  }
};

// Color mapping based on theme slug
const getColorForTheme = (slug: string): string => {
  switch (slug) {
    case 'ai-geletterdheid':
      return 'blue';
    case 'ai-basics':
    case 'ai-literacy':
      return 'blue';
    case 'digitale-geletterdheid':
      return 'purple';
    case 'media-literacy':
    case 'mediawijsheid':
      return 'purple';
    default:
      return 'blue';
  }
};

// Status mapping - for now, all themes from database are available
const getStatusForTheme = (slug: string): 'available' | 'coming-soon' => {
  // You can modify this logic based on your business requirements
  return 'available';
};

export const TrainingThemes: React.FC = () => {
  const [themes, setThemes] = useState<TrainingTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        setLoading(true);
        const dbThemes = await topicsApi.getThemes();
        
        // Transform database themes to TrainingTheme format, excluding digitale-kennistoets
        const transformedThemes: TrainingTheme[] = dbThemes
          .filter(theme => theme.slug !== 'digitale-kennistoets') // Exclude digitale kennistoets from training themes
          .map(theme => ({
            ...theme,
            image: getImageForTheme(theme.slug),
            color: getColorForTheme(theme.slug),
            status: getStatusForTheme(theme.slug),
            shortDescription: getShortDescriptionForTheme(theme.slug),
            path: `/topics?theme=${theme.slug}` // Add theme filter to topics page
          }));

        // Add hardcoded Digitale Geletterdheid theme if not already in database
        const hasDigitalLiteracy = transformedThemes.some(t => t.slug === 'digitale-geletterdheid');
        const digitalLiteracyTheme: TrainingTheme = {
          id: 'digital-literacy-hardcoded',
          slug: 'digitale-geletterdheid',
          title: 'Digitale Geletterdheid',
          description: 'Complete digitale geletterdheid training gebaseerd op Nederlandse standaarden en actuele bronnen.',
          image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop&crop=center',
          color: 'purple',
          status: 'available',
          path: '/topics?theme=digitale-geletterdheid',
          shortDescription: 'Ontwikkel essentiële digitale vaardigheden voor de moderne werkplek.',
          created_at: new Date().toISOString()
        };

        // Add hardcoded Mediawijsheid theme if not already in database
        const hasMediawijsheid = transformedThemes.some(t => t.slug === 'mediawijsheid');
        const mediaLiteracyTheme: TrainingTheme = {
          id: 'media-literacy-hardcoded',
          slug: 'mediawijsheid',
          title: 'Mediawijsheid',
          description: 'Leer kritisch omgaan met media, nepnieuws herkennen en verantwoordelijk mediagebruik.',
          image: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=250&fit=crop&crop=center',
          color: 'purple',
          status: 'available',
          path: `/topics?theme=mediawijsheid`,
          shortDescription: 'Leer kritisch omgaan met media, nepnieuws herkennen en verantwoordelijk mediagebruik.',
          created_at: new Date().toISOString()
        };

        // Only add hardcoded themes if they don't exist in the database
        const additionalThemes = [
          ...(!hasDigitalLiteracy ? [digitalLiteracyTheme] : []),
          ...(!hasMediawijsheid ? [mediaLiteracyTheme] : [])
        ];

        setThemes([...transformedThemes, ...additionalThemes]);
      } catch (err) {
        console.error('Failed to fetch themes:', err);
        setError('Failed to load training themes');
        
        // Fallback to hardcoded themes if API fails
        setThemes([
          {
            id: 'fallback-ai',
            slug: 'ai-geletterdheid',
            title: 'AI Geletterdheid',
            description: 'Leer de fundamenten van kunstmatige intelligentie en hoe je het praktisch kunt toepassen in je werk.',
            image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&crop=center',
            color: 'blue',
            status: 'available',
            shortDescription: 'Leer de fundamenten van kunstmatige intelligentie en hoe je het praktisch kunt toepassen in je werk.',
            path: '/topics',
            created_at: new Date().toISOString()
          },
          {
            id: 'fallback-digital',
            slug: 'digitale-geletterdheid',
            title: 'Digitale Geletterdheid',
            description: 'Complete digitale geletterdheid training gebaseerd op Nederlandse standaarden en actuele bronnen.',
            image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop&crop=center',
            color: 'purple',
            status: 'available',
            path: '/topics?theme=digitale-geletterdheid',
            shortDescription: 'Ontwikkel essentiële digitale vaardigheden voor de moderne werkplek.',
            created_at: new Date().toISOString()
          },
          {
            id: 'fallback-media',
            slug: 'mediawijsheid',
            title: 'Mediawijsheid',
            description: 'Leer kritisch omgaan met media, nepnieuws herkennen en verantwoordelijk mediagebruik.',
            image: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=250&fit=crop&crop=center',
            color: 'purple',
            status: 'available',
            path: '/topics?theme=mediawijsheid',
            shortDescription: 'Leer kritisch omgaan met media, nepnieuws herkennen en verantwoordelijk mediagebruik.',
            created_at: new Date().toISOString()
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchThemes();
  }, []);


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Thema's laden...</span>
      </div>
    );
  }

  if (error && themes.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Opnieuw proberen
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {themes.map((theme, index) => {
        const isAvailable = theme.status === 'available';
        
        const cardContent = (
          <div className="h-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group cursor-pointer flex flex-col">
            {/* Image */}
            <div className="relative h-48 overflow-hidden flex-shrink-0">
              <img
                src={theme.image}
                alt={theme.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              {theme.status === 'coming-soon' && (
                <div className="absolute top-3 right-3 bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>Binnenkort</span>
                </div>
              )}
            </div>
            
            {/* Content */}
            <div className="p-6 flex flex-col flex-grow">
              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                {theme.title}
              </h3>
              
              {/* Short Description */}
              <p className="text-gray-600 mb-4 text-sm leading-relaxed group-hover:text-gray-700 transition-colors flex-grow line-clamp-3">
                {theme.shortDescription}
              </p>
              
              {/* Button */}
              <div className="mt-auto">
                {isAvailable && theme.path ? (
                  <div className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium text-sm flex items-center justify-center group">
                    <span>Start hier</span>
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                ) : (
                  <div className="w-full px-4 py-2 bg-gray-100 text-gray-500 rounded-md cursor-not-allowed font-medium text-sm">
                    Binnenkort beschikbaar
                  </div>
                )}
              </div>
            </div>
          </div>
        );

        return isAvailable && theme.path ? (
          <Link key={theme.id} to={theme.path} className="block h-full">
            {cardContent}
          </Link>
        ) : (
          <div key={theme.id} className="h-full">
            {cardContent}
          </div>
        );
      })}
    </div>
  );
};
